import {createTheme, lighten} from '@mui/material/styles'

import breakpoints from '../dashboard/styles/abstracts/breakpoints.module.scss'
import colors from '../dashboard/styles/abstracts/colors.module.scss'

export const dynamicTheme = organizationCobranding => {
  const primaryColor = organizationCobranding?.primaryColor ||
    colors.everplansPrimary

  const primaryLight = lighten(primaryColor, 0.2)

  const secondaryColor = organizationCobranding?.secondaryColor ||
    colors.everplansSecondary

  const tertiaryColor = organizationCobranding?.tertiaryColor ||
    colors.everplansTertiary

  return createTheme({
    breakpoints: {
      values: {
        mobileSm: parseInt(breakpoints.mobileSm.replace('px', ''), 10),
        mobileMd: parseInt(breakpoints.mobileMd.replace('px', ''), 10),
        mobileLg: parseInt(breakpoints.mobileLg.replace('px', ''), 10),
        tabletSm: parseInt(breakpoints.tabletSm.replace('px', ''), 10),
        tabletMd: parseInt(breakpoints.tabletMd.replace('px', ''), 10),
        tabletLg: parseInt(breakpoints.tabletLg.replace('px', ''), 10),
        desktopSm: parseInt(breakpoints.desktopSm.replace('px', ''), 10),
        desktopMd: parseInt(breakpoints.desktopMd.replace('px', ''), 10),
        desktopLg: parseInt(breakpoints.desktopLg.replace('px', ''), 10),
        desktopXl: parseInt(breakpoints.desktopXl.replace('px', ''), 10)
      }
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: ({ownerState, theme}) => ({
            borderRadius: '24px',
            boxShadow: 'none',
            padding: '0',
            textTransform: 'none',
            '&:active': {
              boxShadow: 'none'
            },
            '&:hover': {
              boxShadow: 'none'
            },
            ['@media (hover: none)']: {
              '&:hover': {
                ...(ownerState?.color && ownerState?.variant !== 'text' && {
                  backgroundColor: theme.palette[ownerState.color]?.main
                })
              }
            },
            ['@media (hover: hover)']: {
              '&:not(.Mui-disabled):hover': {
                ...(ownerState?.variant !== 'text' && {
                  backgroundColor: theme.palette[ownerState.color]?.light
                })
              }
            }
          }),
          text: {
            '&:hover': {
              backgroundColor: 'transparent'
            },
            ['@media (hover: none)']: {
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }
          }
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        },
        styleOverrides: {
          root: {
            padding: '0'
          }
        }
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: colors.bodyText,
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontSize: '0.8125rem',
            letterSpacing: 'normal',
            lineHeight: '166%',
            margin: '3px 0 0'
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            '&.MuiInputLabel-root': {
              color: colors.bodyText,
              fontWeight: '300',
              letterSpacing: 'normal',
              marginBottom: 'unset'
            }
          }
        }
      },
      MuiIconButton: {
        defaultProps: {
          disableRipple: true
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            '&.MuiOutlinedInput-root': {
              background: colors.boxBackground,
              borderRadius: '8px',
              color: colors.bodyText,
              fontSize: '1rem',
              fontWeight: 300,
              letterSpacing: 'normal',
              lineHeight: '113.333%'
            }
          },
          input: {
            color: colors.headerText,
            fontWeight: '400',
            '&.MuiOutlinedInput-input:not(.MuiInputBase-inputMultiline)': {
              padding: '12px'
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: colors.bodyText,
            fontSize: '0.9375rem',
            fontWeight: '300',
            lineHeight: '113.333%',
            opacity: '0.5',
            '&[data-shrink="true"]': {
              display: 'none'
            },
            '&.Mui-focused': {
              color: 'unset'
            }
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: '8px',
            marginTop: '10px',
            '.Mui-selected': {
              color: primaryLight
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            border: `1px solid ${colors.textboxBorder}`
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            height: 'unset',
            lineHeight: '21px',
            textAlign: 'left'
          }
        }
      },
      MuiTypography: {
        defaultProps: {
          textTransform: 'none'
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.tooltipBackground,
            borderRadius: '8px',
            fontSize: '0.625rem',
            fontWeight: '400',
            lineHeight: '140%',
            textAlign: 'center'
          },
          arrow: {
            color: colors.tooltipBackground
          }
        }
      }
    },
    palette: {
      primary: {
        active: colors.buttonActiveBackground,
        boxBackground: colors.bridalHeath,
        main: primaryColor,
        contrastText: colors.buttonTextLight,
        text: colors.headerText,
        bodyText: colors.bodyText
      },
      secondary: {
        main: secondaryColor,
        green: colors.everplansSecondaryGreen
      },
      tertiary: {
        main: tertiaryColor
      },
      cancel: {
        active: colors.buttonCancelActiveBackground,
        contrastText: colors.buttonTextLight,
        disabled: colors.buttonCancelDisabledBackground,
        light: colors.buttonCancelHoverBackground,
        main: colors.buttonCancelBackground
      },
      error: {
        active: colors.buttonRemoveActiveBackground,
        disabled: colors.buttonRemoveDisabledBackground,
        light: colors.buttonRemoveHoverBackground,
        main: colors.everplansRed
      },
      white: {
        main: colors.everplansWhite
      }
    },
    shape: {
      borderRadius: '24px'
    },
    typography: {
      fontFamily: 'proxima-nova, Helvetica, Arial, sans-serif'
    }
  })
}

export default dynamicTheme
